import React from 'react';

function renderChart(chartJson: string, key: number): React.ReactNode {
    try {
        const data = JSON.parse(chartJson);
        if (!data.data || !Array.isArray(data.data)) return null;

        const values = data.data.map((d: any) => Math.abs(Number(d.value) || 0));
        const maxVal = Math.max(...values, 1);

        return (
            <div key={key} className="ai-insight-chart">
                {data.title && (
                    <div className="ai-insight-chart__title">
                        {data.title}
                    </div>
                )}
                <div className="ai-insight-chart__bars">
                    {data.data.map((d: any, i: number) => {
                        const val = Number(d.value) || 0;
                        const pct = (Math.abs(val) / maxVal) * 100;
                        const isNegative = val < 0;
                        return (
                            <div key={i} className="ai-insight-chart__row">
                                <div className="ai-insight-chart__label">{d.label}</div>
                                <div className="ai-insight-chart__bar-track">
                                    <div
                                        className={`ai-insight-chart__bar-fill ${isNegative ? 'ai-insight-chart__bar-fill--negative' : ''}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                <div className="ai-insight-chart__value">
                                    {val.toLocaleString()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    } catch {
        return <div key={key} className="ai-insight-chart__error">Invalid chart data</div>;
    }
}

function renderInline(text: string): React.ReactNode {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        const codeParts = part.split(/(`[^`]+`)/g);
        return codeParts.map((cp, j) => {
            if (cp.startsWith('`') && cp.endsWith('`')) {
                return <code key={`${i}-${j}`} className="ai-insight-inline-code">{cp.slice(1, -1)}</code>;
            }
            return cp;
        });
    });
}

function renderTable(rows: string[], startKey: string): React.ReactNode {
    // Strip only the optional leading/trailing pipe before splitting. Filtering out every
    // empty string instead would drop genuinely empty interior cells (`| a || c |`), shifting
    // the remaining columns left and misaligning body values against their headers.
    const parsed = rows.map(row =>
        row.trim()
            .replace(/^\|/, '')
            .replace(/\|$/, '')
            .split('|')
            .map(c => c.trim())
    );

    const separatorIdx = parsed.findIndex(cells =>
        cells.every(c => /^[-:\s]+$/.test(c))
    );

    const headerRows = separatorIdx > 0 ? parsed.slice(0, separatorIdx) : [];
    const bodyRows = separatorIdx >= 0 ? parsed.slice(separatorIdx + 1) : parsed;

    return (
        <div key={startKey} className="ai-insight-table__wrapper">
            <table className="ai-insight-table">
                {headerRows.length > 0 && (
                    <thead>
                        {headerRows.map((cells, ri) => (
                            <tr key={ri}>
                                {cells.map((cell, ci) => (
                                    <th key={ci}>{renderInline(cell)}</th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                )}
                <tbody>
                    {bodyRows.map((cells, ri) => (
                        <tr key={ri}>
                            {cells.map((cell, ci) => (
                                <td key={ci}>{renderInline(cell)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function renderMarkdown(text: string): React.ReactNode {
    // Extract chart blocks
    const chartBlocks: { placeholder: string; json: string }[] = [];
    let processedText = text.replace(/```chart\s*\n([\s\S]*?)\n```/g, (_, json) => {
        const placeholder = `__CHART_${chartBlocks.length}__`;
        chartBlocks.push({ placeholder, json: json.trim() });
        return placeholder;
    });

    // Extract code blocks (``` ... ```)
    const codeBlocks: { placeholder: string; content: string; lang: string }[] = [];
    processedText = processedText.replace(/```(\w*)\s*\n([\s\S]*?)\n```/g, (_, lang, content) => {
        const placeholder = `__CODE_${codeBlocks.length}__`;
        codeBlocks.push({ placeholder, content, lang: lang || '' });
        return placeholder;
    });

    const lines = processedText.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const trimmed = lines[i].trim();

        // Chart placeholder
        const chartMatch = trimmed.match(/^__CHART_(\d+)__$/);
        if (chartMatch) {
            const idx = parseInt(chartMatch[1], 10);
            const block = chartBlocks[idx];
            if (block) {
                elements.push(renderChart(block.json, i));
            }
            i++;
            continue;
        }

        // Code block placeholder
        const codeMatch = trimmed.match(/^__CODE_(\d+)__$/);
        if (codeMatch) {
            const idx = parseInt(codeMatch[1], 10);
            const block = codeBlocks[idx];
            if (block) {
                elements.push(
                    <pre key={i} className="ai-insight-code-block">
                        <code>{block.content}</code>
                    </pre>
                );
            }
            i++;
            continue;
        }

        // Table: collect consecutive pipe-delimited rows
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            const tableRows: string[] = [];
            while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                tableRows.push(lines[i].trim());
                i++;
            }
            // The while loop above already advanced `i` past the table, and this branch
            // continues without a further increment — so a bare `i` key would collide with
            // the element pushed for the first non-table line. Namespace it.
            elements.push(renderTable(tableRows, `table-${i}`));
            continue;
        }

        // Headers
        if (trimmed.startsWith('### ')) {
            elements.push(<h4 key={i} className="ai-insight-md__h4">{renderInline(trimmed.slice(4))}</h4>);
        } else if (trimmed.startsWith('## ')) {
            elements.push(<h3 key={i} className="ai-insight-md__h3">{renderInline(trimmed.slice(3))}</h3>);
        } else if (trimmed.startsWith('# ')) {
            elements.push(<h2 key={i} className="ai-insight-md__h2">{renderInline(trimmed.slice(2))}</h2>);
        }
        // Bullet points
        else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            elements.push(
                <div key={i} className="ai-insight-md__bullet">{renderInline(trimmed.slice(2))}</div>
            );
        }
        // Numbered lists
        else if (/^\d+\.\s/.test(trimmed)) {
            const content = trimmed.replace(/^\d+\.\s/, '');
            const num = trimmed.match(/^(\d+)\./)?.[1];
            elements.push(
                <div key={i} className="ai-insight-md__numbered"><span className="ai-insight-md__num">{num}.</span> {renderInline(content)}</div>
            );
        }
        // Empty line
        else if (trimmed === '') {
            elements.push(<div key={i} className="ai-insight-md__spacer" />);
        }
        // Regular text
        else {
            elements.push(<div key={i} className="ai-insight-md__text">{renderInline(trimmed)}</div>);
        }

        i++;
    }

    return <>{elements}</>;
}
